import React from 'react'
import useSWR from 'swr'

import { getInvoices } from '../../../api'
import { Invoice } from '../../../types'
import PageRightRailProvider, { usePageRightRail } from '../../ui/layout/pageRightRail/PageRightRailProvider'

const PAGE_SIZE = 10
const RECENT_INVOICES_BASE_URL = '/api/invoices?sort=issueDate&order=desc'

export type RecentInvoicesRailContextValue = {
  invoices: Invoice[]
  activeInvoiceId?: string
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  onLoadMore: () => void
}

const RecentInvoicesRailContext = React.createContext<RecentInvoicesRailContextValue | null>(null)

export function useRecentInvoicesRail(): RecentInvoicesRailContextValue {
  const ctx = React.useContext(RecentInvoicesRailContext)
  if (!ctx) {
    throw new Error('useRecentInvoicesRail must be used within RecentInvoicesPanelProvider')
  }
  return ctx
}

type DataProviderProps = {
  children: React.ReactNode
  activeInvoiceId?: string
}

const RecentInvoicesDataProvider: React.FC<DataProviderProps> = ({ children, activeInvoiceId }) => {
  const { isOpen } = usePageRightRail()
  const [invoices, setInvoices] = React.useState<Invoice[]>([])
  const [skip, setSkip] = React.useState(0)
  const [hasMore, setHasMore] = React.useState(true)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const initialLoadDoneRef = React.useRef(false)

  const swrKey = isOpen ? `${RECENT_INVOICES_BASE_URL}&limit=${PAGE_SIZE}&skip=0` : null
  const { data, isLoading } = useSWR(swrKey, getInvoices)

  React.useEffect(() => {
    if (!isOpen) {
      initialLoadDoneRef.current = false
      return
    }
    setSkip(0)
    setInvoices([])
    setHasMore(true)
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || data == null || initialLoadDoneRef.current) return
    setInvoices(data)
    setSkip(PAGE_SIZE)
    setHasMore(data.length >= PAGE_SIZE)
    initialLoadDoneRef.current = true
  }, [data, isOpen])

  const handleLoadMore = React.useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    try {
      const more = await getInvoices(
        `${RECENT_INVOICES_BASE_URL}&limit=${PAGE_SIZE}&skip=${skip}`
      )
      if (more && more.length > 0) {
        setInvoices((prev) => {
          const existingIds = new Set(prev.map((inv) => inv._id))
          const unique = more.filter((inv: Invoice) => inv._id && !existingIds.has(inv._id))
          return [...prev, ...unique]
        })
        setSkip((s) => s + PAGE_SIZE)
        setHasMore(more.length >= PAGE_SIZE)
      } else {
        setHasMore(false)
      }
    } finally {
      setIsLoadingMore(false)
    }
  }, [hasMore, isLoadingMore, skip])

  const contextValue = React.useMemo(
    () => ({
      invoices,
      activeInvoiceId,
      isLoading: isLoading && invoices.length === 0,
      isLoadingMore,
      hasMore,
      onLoadMore: handleLoadMore,
    }),
    [invoices, activeInvoiceId, isLoading, isLoadingMore, hasMore, handleLoadMore]
  )

  return (
    <RecentInvoicesRailContext.Provider value={contextValue}>
      {children}
    </RecentInvoicesRailContext.Provider>
  )
}

type Props = {
  children: React.ReactNode
  activeInvoiceId?: string
}

const RecentInvoicesPanelProvider: React.FC<Props> = ({ children, activeInvoiceId }) => (
  <PageRightRailProvider>
    <RecentInvoicesDataProvider activeInvoiceId={activeInvoiceId}>
      {children}
    </RecentInvoicesDataProvider>
  </PageRightRailProvider>
)

export default RecentInvoicesPanelProvider

// Re-export rail controls for the trigger
export { usePageRightRail as useRecentInvoicesPanel }
